import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, FileText } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const propertyTypes = [
  "Single Family",
  "Condo",
  "Multi-Family",
  "Historic Brownstone",
];
const foundationTypes = [
  "Poured Concrete",
  "Fieldstone",
  "Crawlspace",
  "Slab-on-Grade",
  "Not sure",
];

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-navy-ink placeholder:text-slate-400 transition-colors focus:border-gold-deep focus:outline-none focus:ring-2 focus:ring-gold-deep/20";

const labelCls = "mb-1.5 block text-sm font-medium text-slate-700";

const empty = {
  name: "",
  email: "",
  phone: "",
  address: "",
  zip: "",
  property_type: "",
  preferred_date: "",
  radon_level: "",
  foundation_type: "",
  notes: "",
};

export default function Booking() {
  const [tab, setTab] = useState("test");
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const switchTab = (next) => {
    setTab(next);
    setSubmitted(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        ...form,
        request_type: tab,
      });
      if (!res.data || !res.data.id) throw new Error("unexpected response");
      setSubmitted(true);
      setForm(empty);
      toast.success("Request received — we'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-gold-deep">
              Get started
            </p>
            <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl">
              Book a test — or get a free quote
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-slate-600">
              Tell us a little about your home and we'll take it from there.
              Quotes are always free; no obligation either way.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/60 sm:p-8">
            <div className="grid grid-cols-1 gap-2 rounded-xl bg-slate-100 p-1.5 sm:grid-cols-2">
              <button
                data-testid="booking-form-tab-test"
                onClick={() => switchTab("test")}
                className={`flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold transition-all ${
                  tab === "test"
                    ? "bg-white text-navy shadow"
                    : "text-slate-500 hover:text-navy"
                }`}
              >
                <CalendarCheck className="h-4 w-4" />
                Book a Radon Test
              </button>
              <button
                data-testid="booking-form-tab-quote"
                onClick={() => switchTab("quote")}
                className={`flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold transition-all ${
                  tab === "quote"
                    ? "bg-white text-navy shadow"
                    : "text-slate-500 hover:text-navy"
                }`}
              >
                <FileText className="h-4 w-4" />
                Free Mitigation Quote
              </button>
            </div>

            {submitted ? (
              <div
                data-testid="booking-form-success-message"
                className="mt-8 rounded-xl border border-gold/40 bg-gold/10 p-8 text-center"
              >
                <CalendarCheck className="mx-auto h-10 w-10 text-gold-deep" />
                <h3 className="mt-4 font-display text-xl font-semibold text-navy">
                  Request received
                </h3>
                <p className="mx-auto mt-2 max-w-sm text-sm text-slate-600">
                  Thanks — we have your {tab === "test" ? "test booking" : "quote"}{" "}
                  request and will reach out shortly to confirm details.
                </p>
                <button
                  data-testid="booking-form-reset-btn"
                  onClick={() => setSubmitted(false)}
                  className="mt-6 rounded-lg bg-navy px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-navy-deep"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                {tab === "test" ? (
                  <div className="sm:col-span-2 sm:flex sm:items-center sm:justify-between sm:gap-6 sm:rounded-xl sm:bg-limestone sm:p-4">
                    <p className="text-sm text-slate-600">
                      <span className="font-semibold text-navy">Booking a test:</span>{" "}
                      we'll place a continuous monitor in your home and return a
                      certified report.
                    </p>
                  </div>
                ) : (
                  <div className="sm:col-span-2 sm:rounded-xl sm:bg-limestone sm:p-4">
                    <p className="text-sm text-slate-600">
                      <span className="font-semibold text-navy">Free quote:</span>{" "}
                      tell us what you know — we'll size the system and quote the
                      work, free.
                    </p>
                  </div>
                )}

                <div>
                  <label className={labelCls} htmlFor="bk-name">Full name *</label>
                  <input
                    id="bk-name"
                    data-testid="booking-form-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    className={inputCls}
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="bk-phone">Phone *</label>
                  <input
                    id="bk-phone"
                    data-testid="booking-form-phone-input"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    className={inputCls}
                    placeholder="(508) 555-0000"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="bk-email">Email *</label>
                  <input
                    id="bk-email"
                    data-testid="booking-form-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    className={inputCls}
                    placeholder="jane@email.com"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="bk-address">
                    {tab === "test" ? "Property address *" : "Property address"}
                  </label>
                  <input
                    id="bk-address"
                    data-testid="booking-form-address-input"
                    required={tab === "test"}
                    value={form.address}
                    onChange={set("address")}
                    className={inputCls}
                    placeholder="123 Example St, Boston"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="bk-zip">Zip code *</label>
                  <input
                    id="bk-zip"
                    data-testid="booking-form-zip-input"
                    type="text"
                    inputMode="numeric"
                    maxLength="5"
                    required
                    value={form.zip}
                    onChange={set("zip")}
                    className={inputCls}
                    placeholder="02108"
                  />
                </div>

                {tab === "test" ? (
                  <div>
                    <label className={labelCls} htmlFor="bk-property">
                      Property type
                    </label>
                    <select
                      id="bk-property"
                      data-testid="booking-form-property-type-select"
                      value={form.property_type}
                      onChange={set("property_type")}
                      className={inputCls}
                    >
                      <option value="">Select…</option>
                      {propertyTypes.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className={labelCls} htmlFor="bk-foundation">
                      Foundation type
                    </label>
                    <select
                      id="bk-foundation"
                      data-testid="booking-form-foundation-type-select"
                      value={form.foundation_type}
                      onChange={set("foundation_type")}
                      className={inputCls}
                    >
                      <option value="">Select…</option>
                      {foundationTypes.map((f) => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                  </div>
                )}

                {tab === "test" ? (
                  <div>
                    <label className={labelCls} htmlFor="bk-date">
                      Preferred test date
                    </label>
                    <input
                      id="bk-date"
                      data-testid="booking-form-date-input"
                      type="date"
                      value={form.preferred_date}
                      onChange={set("preferred_date")}
                      className={inputCls}
                    />
                  </div>
                ) : (
                  <div>
                    <label className={labelCls} htmlFor="bk-level">
                      Known radon level (pCi/L)
                    </label>
                    <input
                      id="bk-level"
                      data-testid="booking-form-radon-level-input"
                      type="text"
                      value={form.radon_level}
                      onChange={set("radon_level")}
                      className={inputCls}
                      placeholder="e.g. 5.8"
                    />
                  </div>
                )}

                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="bk-notes">
                    {tab === "test" ? "Anything we should know?" : "Notes"}
                  </label>
                  <textarea
                    id="bk-notes"
                    data-testid="booking-form-notes-input"
                    rows="3"
                    value={form.notes}
                    onChange={set("notes")}
                    className={inputCls}
                    placeholder={
                      tab === "test"
                        ? "Reason for testing, access details, timing…"
                        : "Test report details, closing dates, questions…"
                    }
                  />
                </div>

                <div className="sm:col-span-2">
                  <button
                    data-testid="booking-form-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="w-full rounded-lg bg-navy px-8 py-4 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-navy-deep disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting
                      ? "Sending…"
                      : tab === "test"
                        ? "Book My Radon Test"
                        : "Request My Free Quote"}
                  </button>
                  <p className="mt-3 text-center text-xs text-slate-400">
                    Free quotes, always. We reply to every request personally.
                  </p>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
